/* eslint-disable no-useless-escape */
import SVG from "react-inlinesvg";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import { SetUpProviderPasswordType } from "../../../Store/Provider/types";
import { forwardRef, useEffect, useState, useImperativeHandle } from "react";
import { handleTempStates } from "../../../Methods/Helpers/TempStateHelper";
import { TempStore } from "../../../Store/TempStore/types";
import { selectTempState } from "../../../Store/TempStore/selectors";

type RegisterFormType = {
  onSubmitDetails: (email: string, password: string) => void;
}

type ResetPassFocusStateType = {
  password: boolean;
  confirmPassword: boolean;
}
const initialValues: SetUpProviderPasswordType = {
  password: "",
  confirmPassword: "",
  email: "",
}

const validationSchema = Yup.object({
  email: Yup.string().email("Please enter valid email address").required("Email is required"),
  password: Yup.string().required("Password is required").min(8, 'Password is too short - should be 8 chars minimum.')
  .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])/, "Must contain one uppercase, one lowercase, one number and one special case character"),
  confirmPassword: Yup.string().required("Confirm password is required").oneOf([Yup.ref('password'), null], 'Passwords must match'),
});

const RegisterForm: React.ForwardRefExoticComponent<React.RefAttributes<unknown> & RegisterFormType> = forwardRef((props, ref) => {
  const dispatch = useAppDispatch();
  const tempState: TempStore = useAppSelector(selectTempState);
  const [isFocused, setIsFocused] = useState<ResetPassFocusStateType>({ password: false, confirmPassword: false });
  const {onSubmitDetails} = props;

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    onSubmit: (values)=>{
      onSubmitDetails(values.email, values.password);
    }
  });

  useImperativeHandle(ref, () => ({
    clearForm(){
      formik.setErrors({});
      formik.setTouched({});
      formik.resetForm();
    }
  }));

  useEffect(() => {
    if(tempState?.register?.email){
      formik.setTouched({...formik.touched, email: true});
      formik.setValues({...formik.values, email: tempState?.register?.email}, true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (e: React.ChangeEvent<any>) => {
    let tempStateHandler: any = handleTempStates("register", {...tempState}, e.target.name, e.target.value);
    dispatch(tempStateHandler.actionType(tempStateHandler.key));
    formik.handleChange(e);
  }
  const handleBlur = (e: any, focusFor: string) => {
    setIsFocused({ ...isFocused, [focusFor]: false });
    formik.handleBlur(e);
  };
  
  return (
    <>
      <form
        className=""
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit();
          return false;
        }}
        noValidate
        autoComplete="off"
      >
        <div className={`user-detail active`}>
          <div className="floating-input-container">
            <input
              placeholder=" "
              name="email"
              className="form-control"
              type="email"
              autoComplete="off"
              value={formik.values.email}
              onChange={(e)=>handleFieldChange(e)}
              onKeyUp={formik.handleBlur}
              onBlur={formik.handleBlur}
            />
            <label className="float-label d-flex" htmlFor="">
              Email  
            </label>
            {formik.touched.email && !formik.errors.email &&
              <span className="tick-validate right-position">
              <SVG
                className="tick-icon"
                src="Assets/Images/Icons/tick-icon.svg"
                width="16"
                height="16"
              />
            </span>}
            
            {formik.touched.email && formik.errors.email ? (
              <label className="text-error mt-2">
                {formik.errors.email}
              </label>
            ) : ("")}
          </div>
          <div className="floating-input-container mt-4">
            <div className={`password-inner-field ${isFocused.password || formik.values.password ? 'focused' : ''}`} style={{ ...{ borderColor: isFocused.password ? "#66CFC8" : "#C4C4C4"}}}>
              <input
                placeholder=" "
                name="password"
                className="form-control"
                type="password"
                autoComplete="off"
                onFocus={() => setIsFocused({ ...isFocused, password: true })}
                value={formik.values.password}
                onChange={formik.handleChange}
                onKeyUp={formik.handleBlur} 
                onBlur={(e: any) => handleBlur(e, "password")}
              />
              
              </div>

            <label className="float-label d-flex" htmlFor="">
              Create password
            </label>
            {formik.touched.password && !formik.errors.password ?
              ( 
              <span className="tick-validate right-position">
              <SVG
                className="tick-icon"
                src="Assets/Images/Icons/tick-icon.svg"
                width="16"
                height="16"
              />
              </span> 
              ) : ( 
              <div className="info-content">
                <span className="info-icon position-right show-info-on-hover">
                  <SVG
                    className="tooltip-icon"
                    src="Assets/Images/Icons/Tooltip.svg"
                    width="26"
                    height="26"
                  />
                </span>
                <div className="password-info-content card">
                  <b className="theme-text">Password Policy:</b>
                  <ul className="ps-3">
                    <li>Minimum length of 8 characters</li>
                  </ul>
                  <b className="theme-text">
                    Contain at least 1 of the following:
                  </b>
                  <ul className="ps-3 mb-0">
                    <li>One uppercase character</li>
                    <li>One lowercase character </li>
                    <li>One special character</li>
                    <li>One number</li>
                  </ul>
                </div>
              </div>
            )}

            {formik.touched.password &&
            formik.errors.password ? (
              <label className="text-error mt-2">
                {formik.errors.password}
              </label>
            ) : (
              ""
            )}
          </div>

          <div className="floating-input-container confirm-password mt-4">
            <div className={`password-inner-field ${isFocused.confirmPassword || formik.values.confirmPassword ? 'focused' : ''}`} style={{ ...{ borderColor: isFocused.confirmPassword ? "#66CFC8" : "#C4C4C4" } }}>
                <input
                  placeholder=" "
                  name="confirmPassword"
                  className="form-control"
                  type="password"
                  autoComplete="off"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onKeyUp={formik.handleBlur}
                  onBlur={(e: any) => handleBlur(e, "confirmPassword")}
                  onFocus={() => setIsFocused({ ...isFocused, confirmPassword: true })}
                /> 
              </div>

            <label className="float-label d-flex" htmlFor="">
              Confirm password
            </label>
            {formik.touched.confirmPassword && !formik.errors.confirmPassword &&
              <span className="tick-validate right-position">
                <SVG
                  className="tick-icon"
                  src="Assets/Images/Icons/tick-icon.svg"
                  width="16"
                  height="16"
                />
              </span>
            }
            {formik.touched.confirmPassword &&
            formik.errors.confirmPassword ? (
              <label className="text-error mt-2">
                {formik.errors.confirmPassword}
              </label>
            ) : (
              ""
            )}
          </div>
          <div className="btn-wrap btn-top-space">
            <button type="submit" className={`btn ${!formik.isValid ? "btn-custom-disabled" : "btn-success"}`} disabled={!formik.isValid}>
              Sign Up
            </button>
          </div>
        </div>
      </form>
    </>
  )
})

export default RegisterForm;